// shared/pdf/components/PdfTable.jsx
import React from 'react';
import { View, Text, StyleSheet } from '@react-pdf/renderer';

const ROW_H = 16;
const GRID_WIDTH = 0.5;

const base = StyleSheet.create({
  headRow:  { flexDirection: 'row', minHeight: ROW_H },
  bodyRow:  { flexDirection: 'row', minHeight: ROW_H },
  footRow:  { flexDirection: 'row', minHeight: ROW_H + 2 },
  cell:     { paddingHorizontal: 4, paddingVertical: 3, fontSize: 8, fontFamily: 'NotoSans', overflow: 'hidden' },
  headCell: { paddingHorizontal: 4, paddingVertical: 3, fontSize: 7.5, fontFamily: 'NotoSans', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: 0.5 },
  footCell: { paddingHorizontal: 4, paddingVertical: 4, fontSize: 8.5, fontFamily: 'NotoSans', fontWeight: 'bold' },
});

// Only active when theme.rule is set (currently just the `minimal` theme).
// Every cell gets a right border (the last cell's right border becomes the
// table's right edge); the first cell in each row also gets a left border
// (the table's left edge). Combined with gridRowStyle's top/bottom borders,
// this forms a full grid without needing a wrapping container View, since
// PdfTableHead/Row/Foot are rendered as flat siblings by callers.
function gridCellStyle(theme, i) {
  if (!theme.rule) return null;
  return {
    borderRightWidth: GRID_WIDTH,
    borderRightColor: theme.rule,
    ...(i === 0 ? { borderLeftWidth: GRID_WIDTH, borderLeftColor: theme.rule } : {}),
  };
}

function gridRowStyle(theme, { top = false, bottom = false } = {}) {
  if (!theme.rule) return null;
  const style = {};
  if (top)    { style.borderTopWidth    = GRID_WIDTH; style.borderTopColor    = theme.rule; }
  if (bottom) { style.borderBottomWidth = GRID_WIDTH; style.borderBottomColor = theme.rule; }
  return style;
}

// cols: [{ label, flex?, width?, align? }]
// values: string[]
export function PdfTableHead({ cols, theme }) {
  return (
    <View style={[base.headRow, { backgroundColor: theme.dark }, gridRowStyle(theme, { top: true, bottom: true })]} wrap={false}>
      {cols.map((col, i) => (
        <Text
          key={i}
          style={[base.headCell, { flex: col.flex || 1, width: col.width, textAlign: col.align || 'left', color: theme.headerText }, gridCellStyle(theme, i)]}
        >
          {col.label}
        </Text>
      ))}
    </View>
  );
}

export function PdfTableRow({ cols, values, index, theme, highlight }) {
  const bg = highlight === 'danger'  ? theme.mid
           : highlight === 'success' ? theme.light
           : index % 2 === 0 ? theme.white : theme.light;
  return (
    <View style={[base.bodyRow, { backgroundColor: bg }, gridRowStyle(theme, { bottom: true })]} wrap={false}>
      {cols.map((col, i) => (
        <Text
          key={i}
          style={[base.cell, { flex: col.flex || 1, width: col.width, textAlign: col.align || 'left', color: col.color || theme.body }, gridCellStyle(theme, i)]}
        >
          {String(values[i] ?? '—')}
        </Text>
      ))}
    </View>
  );
}

export function PdfTableFoot({ cols, values, theme }) {
  return (
    <View style={[base.footRow, { backgroundColor: theme.mid }, gridRowStyle(theme, { top: true, bottom: true })]} wrap={false}>
      {cols.map((col, i) => (
        <Text
          key={i}
          style={[base.footCell, { flex: col.flex || 1, width: col.width, textAlign: col.align || 'left', color: theme.body }, gridCellStyle(theme, i)]}
        >
          {String(values[i] ?? '')}
        </Text>
      ))}
    </View>
  );
}

export { gridCellStyle, gridRowStyle };
